/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { TransitionProps } from "notistack";
import { useNotification } from "../../context/notification-provider";
import { updateClientScoringRule } from "./service";
import { ClientScoringRule } from "./types";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface EditClientScoringRuleDialogProps {
  open: boolean;
  onClose: () => void;
  clientScoringRule: ClientScoringRule | null;
  clientId: number | null;
  clientName: string;
}

const EditClientScoringRuleDialog: React.FC<
  EditClientScoringRuleDialogProps
> = ({ open, onClose, clientScoringRule, clientId, clientName }) => {
  const [disabled, setDisabled] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { showNotification } = useNotification();

  React.useEffect(() => {
    if (clientScoringRule) {
      setDisabled(clientScoringRule.disabled);
    }
  }, [clientScoringRule]);

  const handleSubmit = async () => {
    if (!clientScoringRule || clientId === null) return;

    setIsSubmitting(true);
    try {
      await updateClientScoringRule({
        clientId,
        scoringRuleId: clientScoringRule.scoringRule.id,
        ignored: disabled,
      });
      showNotification({
        message: "Client scoring rule updated successfully.",
        severity: "success",
        duration: 3000,
      });
      onClose();
    } catch (err) {
      showNotification({
        message: `Failed to update client scoring rule. Reason: ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!clientScoringRule) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slots={{ transition: Transition }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Client Scoring Rule
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Client:</strong> {clientName}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Rule Type:</strong> {clientScoringRule.scoringRule.ruleType}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Description:</strong>{" "}
            {clientScoringRule.scoringRule.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Score:</strong> {clientScoringRule.scoringRule.score}
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={!disabled}
              onChange={(e) => setDisabled(!e.target.checked)}
            />
          }
          label="Enabled for this Client"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          Update Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditClientScoringRuleDialog;
